/**
 * RateLimitingSettings.jsx
 * Admin panel for managing rate limits
 */

import React, { useEffect, useMemo, useState } from "react";
import api from "../../shared/api/apiClient";
import { useTranslation } from "react-i18next";
import { getUserRole } from "../../shared/lib/auth/authHelper";
import "./RateLimitingSettings.css";

const formatErrorMessage = (err, fallback) => {
  const detail = err?.response?.data?.detail;

  if (!detail) return fallback;
  if (typeof detail === "string") return detail;

  if (Array.isArray(detail)) {
    return detail.map((d) => d?.msg || JSON.stringify(d)).join("; ");
  }

  if (typeof detail === "object") {
    return detail.msg || JSON.stringify(detail);
  }

  try {
    return String(detail);
  } catch {
    return fallback;
  }
};

export function RateLimitingSettings() {
  const { t } = useTranslation();

  const FIELD_GROUPS = useMemo(
    () => [
      {
        title: t("crm_settings_rate_limit_group_general", {
          defaultValue: "General",
        }),
        fields: [
          [
            "rate_limit_general",
            t("crm_settings_rate_limit_field_general_api", {
              defaultValue: "General API",
            }),
            "100/min",
          ],
          [
            "rate_limit_public",
            t("crm_settings_rate_limit_field_public_endpoints", {
              defaultValue: "Public Endpoints",
            }),
            "20/min",
          ],
        ],
      },
      {
        title: t("crm_settings_rate_limit_group_auth", {
          defaultValue: "Auth",
        }),
        fields: [
          [
            "rate_limit_auth_login",
            t("crm_settings_rate_limit_field_login", {
              defaultValue: "Login",
            }),
            "5/min",
          ],
          [
            "rate_limit_auth_register",
            t("crm_settings_rate_limit_field_register", {
              defaultValue: "Register",
            }),
            "5/min",
          ],
          [
            "rate_limit_auth_refresh",
            t("crm_settings_rate_limit_field_refresh_token", {
              defaultValue: "Refresh Token",
            }),
            "10/min",
          ],
          [
            "rate_limit_auth_password_reset",
            t("crm_settings_rate_limit_field_password_reset", {
              defaultValue: "Password Reset",
            }),
            "3/min",
          ],
        ],
      },
      {
        title: t("crm_settings_rate_limit_group_email", {
          defaultValue: "Email",
        }),
        fields: [
          [
            "rate_limit_email_send",
            t("crm_settings_rate_limit_field_send_email", {
              defaultValue: "Send Email",
            }),
            "50/hour",
          ],
          [
            "rate_limit_email_bulk",
            t("crm_settings_rate_limit_field_bulk_email", {
              defaultValue: "Bulk Email",
            }),
            "10/hour",
          ],
          [
            "rate_limit_email_fetch",
            t("crm_settings_rate_limit_field_fetch_email", {
              defaultValue: "Fetch Email",
            }),
            "60/hour",
          ],
        ],
      },
      {
        title: t("crm_settings_rate_limit_group_telephony", {
          defaultValue: "Telephony",
        }),
        fields: [
          [
            "rate_limit_telephony_call",
            t("crm_settings_rate_limit_field_calls", {
              defaultValue: "Calls",
            }),
            "30/min",
          ],
          [
            "rate_limit_telephony_token",
            t("crm_settings_rate_limit_field_telephony_token", {
              defaultValue: "Telephony Token",
            }),
            "20/min",
          ],
        ],
      },
      {
        title: t("crm_settings_rate_limit_group_chat", {
          defaultValue: "Chat",
        }),
        fields: [
          [
            "rate_limit_chat_message",
            t("crm_settings_rate_limit_field_chat_messages", {
              defaultValue: "Chat Messages",
            }),
            "60/min",
          ],
          [
            "rate_limit_chat_thread",
            t("crm_settings_rate_limit_field_chat_threads", {
              defaultValue: "Chat Threads",
            }),
            "20/min",
          ],
        ],
      },
      {
        title: t("crm_settings_rate_limit_group_reports", {
          defaultValue: "Reports",
        }),
        fields: [
          [
            "rate_limit_reports_export",
            t("crm_settings_rate_limit_field_export_reports", {
              defaultValue: "Export Reports",
            }),
            "5/hour",
          ],
          [
            "rate_limit_reports_generate",
            t("crm_settings_rate_limit_field_generate_reports", {
              defaultValue: "Generate Reports",
            }),
            "10/hour",
          ],
        ],
      },
      {
        title: t("crm_settings_rate_limit_group_ai", {
          defaultValue: "AI",
        }),
        fields: [
          [
            "rate_limit_ai_summary",
            t("crm_settings_rate_limit_field_ai_summary", {
              defaultValue: "AI Summary",
            }),
            "20/min",
          ],
          [
            "rate_limit_ai_score",
            t("crm_settings_rate_limit_field_ai_score", {
              defaultValue: "AI Score",
            }),
            "20/min",
          ],
        ],
      },
    ],
    [t]
  );

  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [edited, setEdited] = useState({});
  const [accessDenied, setAccessDenied] = useState(false);

  const isSuperAdmin =
    String(getUserRole() || "").trim().toLowerCase() === "superadmin";

  useEffect(() => {
    fetchConfig();
  }, []);

  const clearMessages = () => {
    setError("");
    setSuccess("");
  };

  const fetchConfig = async () => {
    setLoading(true);

    try {
      const response = await api.get("/api/system/rate-limits/config");
      setConfig(response.data);
      setEdited({});
      setError("");
      setSuccess("");
      setAccessDenied(false);
    } catch (err) {
      console.error("Error fetching config:", err);

      if (err?.response?.status === 403) {
        setAccessDenied(true);
        setError(
          formatErrorMessage(
            err,
            t("rate_limit_access_not_enabled", {
              defaultValue:
                "Admin access to rate limits is not enabled. Please contact SuperAdmin to grant permission.",
            })
          )
        );
      } else {
        setError(
          formatErrorMessage(
            err,
            t("rate_limit_failed_load_config", {
              defaultValue:
                "Failed to load configuration. Please check your permissions and try again.",
            })
          )
        );
      }

      setConfig(null);
      setEdited({});
      setSuccess("");
    } finally {
      setLoading(false);
    }
  };

  const handleFieldChange = (fieldName, value) => {
    clearMessages();
    setEdited((prev) => ({
      ...prev,
      [fieldName]: value,
    }));
  };

  const handleSave = async () => {
    clearMessages();
    setSaving(true);

    try {
      const response = await api.put("/api/system/rate-limits/config", edited);
      setConfig(response.data);
      setEdited({});
      setSuccess(
        t("rate_limit_configuration_saved", {
          defaultValue: "Configuration saved successfully.",
        })
      );
      setTimeout(() => setSuccess(""), 3000);
    } catch (err) {
      console.error("Error saving config:", err);
      setError(
        formatErrorMessage(
          err,
          t("rate_limit_error_saving_configuration", {
            defaultValue: "Error saving configuration",
          })
        )
      );
    } finally {
      setSaving(false);
    }
  };

  const handleReset = async () => {
    const confirmed = window.confirm(
      t("rate_limit_confirm_reset_defaults", {
        defaultValue: "Reset all limits to default values? This cannot be undone.",
      })
    );
    if (!confirmed) return;

    clearMessages();
    setSaving(true);

    try {
      const response = await api.post("/api/system/rate-limits/reset-to-default");
      setConfig(response.data);
      setEdited({});
      setSuccess(
        t("rate_limit_reset_success", {
          defaultValue: "Limits reset to default values.",
        })
      );
      setTimeout(() => setSuccess(""), 3000);
    } catch (err) {
      console.error("Error resetting:", err);
      setError(
        formatErrorMessage(
          err,
          t("rate_limit_error_resetting", {
            defaultValue: "Error resetting limits",
          })
        )
      );
    } finally {
      setSaving(false);
    }
  };

  const handleToggle = async (checked) => {
    clearMessages();
    setSaving(true);

    try {
      await api.post("/api/system/rate-limits/toggle", null, {
        params: { enabled: checked },
      });

      setConfig((prev) => ({
        ...prev,
        rate_limiting_enabled: checked,
      }));

      setSuccess(
        checked
          ? t("rate_limit_enabled_success", {
              defaultValue: "Rate limiting is now enabled.",
            })
          : t("rate_limit_disabled_success", {
              defaultValue: "Rate limiting is now disabled.",
            })
      );
      setTimeout(() => setSuccess(""), 3000);
    } catch (err) {
      console.error("Error toggling:", err);
      setError(
        formatErrorMessage(
          err,
          t("rate_limit_error_toggling", {
            defaultValue: "Error toggling rate limiting",
          })
        )
      );
    } finally {
      setSaving(false);
    }
  };

  const hasChanges = useMemo(() => Object.keys(edited).length > 0, [edited]);

  const getValue = (fieldName) => {
    if (edited[fieldName] !== undefined) return edited[fieldName];
    return config?.[fieldName] ?? "";
  };

  if (loading) {
    return (
      <div className="rateCompactShell">
        <div className="rateCompactLoading">
          {t("loading", { defaultValue: "Loading" })}...
        </div>
      </div>
    );
  }

  if (accessDenied) {
    return (
      <div className="rateCompactShell">
        <div className="rateCompactSectionCard">
          <div className="rateCompactTopbar">
            <div className="rateCompactHeading">
              <div className="rateCompactTitleRow">
                <h2 className="rateCompactTitle">
                  {t("rate_limiting_settings", {
                    defaultValue: "Rate Limiting",
                  })}
                </h2>
              </div>
              <div className="rateCompactSubtitle">
                {t("rate_limiting_settings_subtitle", {
                  defaultValue: "Real limits from backend configuration.",
                })}
              </div>
            </div>

            <div className="rateCompactActions">
              <button
                type="button"
                className="secondaryActionCompact"
                onClick={fetchConfig}
                disabled={saving}
              >
                {t("refresh", { defaultValue: "Refresh" })}
              </button>
            </div>
          </div>
        </div>

        <div className="rate-settings-alert rate-settings-alert-danger">
          <span className="alert-icon">🔒</span>
          <div>
            <strong>
              {t("access_denied", { defaultValue: "Access denied" })}
            </strong>
            <div style={{ marginTop: 4 }}>{error}</div>
          </div>
        </div>
      </div>
    );
  }

  if (!config) {
    return (
      <div className="rateCompactShell">
        <div className="rate-settings-alert rate-settings-alert-danger">
          <span className="alert-icon">⚠️</span>
          <div>
            <strong>
              {t("rate_limit_unable_load_settings", {
                defaultValue: "Unable to load rate limit settings",
              })}
            </strong>
            <div style={{ marginTop: 4 }}>
              {error ||
                t("rate_limit_unexpected_load_error", {
                  defaultValue: "An unexpected error occurred while loading the page.",
                })}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="rateCompactShell">
      {error ? (
        <div className="rate-settings-alert rate-settings-alert-danger">
          <span className="alert-icon">⚠️</span>
          <div>{error}</div>
        </div>
      ) : null}

      {success ? (
        <div className="rate-settings-alert rate-settings-alert-success">
          <span className="alert-icon">✅</span>
          <div>{success}</div>
        </div>
      ) : null}

      <section className="rateCompactSectionCard">
        <div className="rateCompactSectionHeader">
          <div className="rateCompactSectionHeaderMain">
            <div className="rateCompactSectionTitleRow">
              <h2 className="rateCompactSectionMainTitle">
                {t("general_access", { defaultValue: "General Access" })}
              </h2>
            </div>
            <div className="rateCompactSectionSubtext">
              {t("general_access_subtitle", {
                defaultValue:
                  "Global permissions and system-level control for rate limiting.",
              })}
            </div>
          </div>
          <span className="compactPill">
            {t("rate_limit_scope_system", {
              defaultValue: "System",
            })}
          </span>
        </div>

        <div className="rateCompactToggleGrid">
          <div className="rateCompactToggleItem">
            <div className="rateCompactToggleMeta">
              <div className="rateCompactToggleLabel">
                {t("rate_limiting_enabled", {
                  defaultValue: "Enable Rate Limiting",
                })}
              </div>
              <div className="rateCompactToggleHint">
                {t("rate_limiting_enabled_help", {
                  defaultValue: "Globally enable or disable rate limiting.",
                })}
              </div>
            </div>

            <label className="rateCompactToggleControl">
              <input
                type="checkbox"
                checked={!!config.rate_limiting_enabled}
                onChange={(e) => handleToggle(e.target.checked)}
                disabled={saving}
              />
              <span>
                {config.rate_limiting_enabled
                  ? t("crm_settings_on", { defaultValue: "On" })
                  : t("crm_settings_off", { defaultValue: "Off" })}
              </span>
            </label>
          </div>

          {isSuperAdmin && (
            <div className="rateCompactToggleItem">
              <div className="rateCompactToggleMeta">
                <div className="rateCompactToggleLabel">
                  {t("allow_admins_manage_rate_limits", {
                    defaultValue: "Allow Admin to Modify",
                  })}
                </div>
                <div className="rateCompactToggleHint">
                  {t("allow_admins_manage_rate_limits_help", {
                    defaultValue:
                      "Permit admins to view and change rate limit settings.",
                  })}
                </div>
              </div>

              <label className="rateCompactToggleControl">
                <input
                  type="checkbox"
                  checked={
                    !!(
                      edited.allow_admins_manage_rate_limits ??
                      config.allow_admins_manage_rate_limits
                    )
                  }
                  onChange={(e) =>
                    handleFieldChange(
                      "allow_admins_manage_rate_limits",
                      e.target.checked
                    )
                  }
                  disabled={saving}
                />
                <span>
                  {(edited.allow_admins_manage_rate_limits ??
                    config.allow_admins_manage_rate_limits)
                    ? t("crm_settings_on", { defaultValue: "On" })
                    : t("crm_settings_off", { defaultValue: "Off" })}
                </span>
              </label>
            </div>
          )}
        </div>

        {!isSuperAdmin && !config.allow_admins_manage_rate_limits && (
          <div className="rateCompactMutedNotice">
            {t("rate_limit_superadmin_only_notice", {
              defaultValue:
                "Only SuperAdmin can modify rate limits. Ask your SuperAdmin to enable admin modifications.",
            })}
          </div>
        )}
      </section>

      <section className="rateCompactSectionCard">
        <div className="rateCompactTopbar">
          <div className="rateCompactHeading">
            <div className="rateCompactTitleRow">
              <h2 className="rateCompactTitle">
                {t("rate_limit_rules_title", {
                  defaultValue: "Rate Limit Rules",
                })}
              </h2>
            </div>
            <div className="rateCompactSubtitle">
              {t("rate_limiting_settings_subtitle", {
                defaultValue: "Real limits from backend configuration.",
              })}
            </div>
          </div>

          <div className="rateCompactActions">
            <button
              type="button"
              className="secondaryActionCompact"
              onClick={handleReset}
              disabled={saving}
            >
              {t("reset_defaults", { defaultValue: "Defaults" })}
            </button>

            <button
              type="button"
              className="secondaryActionCompact"
              onClick={() => {
                setEdited({});
                clearMessages();
                fetchConfig();
              }}
              disabled={saving}
            >
              {t("refresh", { defaultValue: "Refresh" })}
            </button>

            <button
              type="button"
              className="primaryActionCompact"
              onClick={handleSave}
              disabled={saving || !hasChanges}
            >
              {saving
                ? t("saving", { defaultValue: "Saving..." })
                : t("save_changes", { defaultValue: "Save changes" })}
            </button>
          </div>
        </div>

        <details className="rateCompactAccordion" open>
          <summary className="rateCompactAccordionSummary">
            <div className="rateCompactAccordionSummaryMain">
              <span className="rateCompactAccordionTitle">
                {t("show_rate_limit_rules", {
                  defaultValue: "Show rate limit rules",
                })}
              </span>
              <span className="rateCompactAccordionHint">
                {t("show_rate_limit_rules_hint", {
                  defaultValue:
                    "General, Auth, Email, Telephony, Chat, Reports and AI limits",
                })}
              </span>
            </div>
            <span className="rateCompactAccordionChevron"><i class="lni lni-chevron-down"></i></span>
          </summary>

          <div className="rateCompactAccordionBody">
            <div className="rateCompactFieldsViewport">
              <div className="rateCompactGrid">
                {FIELD_GROUPS.map((group) => (
                  <section key={group.title} className="rateCompactCard">
                    <div className="rateCompactCardHead">
                      <div className="rateCompactCardTitle">{group.title}</div>
                    </div>

                    <div className="rateCompactFieldList">
                      {group.fields.map(([field, label, example]) => (
                        <div key={field} className="rateCompactFieldRow">
                          <div className="rateCompactFieldMeta">
                            <label className="rateCompactFieldLabel">
                              {label}
                            </label>
                            <span className="rateCompactFieldHint">
                              {config?.[field] || example}
                            </span>
                          </div>

                          <input
                            className="rateCompactInput"
                            type="text"
                            value={getValue(field)}
                            onChange={(e) =>
                              handleFieldChange(field, e.target.value)
                            }
                            disabled={saving}
                            placeholder={example}
                          />
                        </div>
                      ))}
                    </div>
                  </section>
                ))}
              </div>
            </div>
          </div>
        </details>
      </section>

      {hasChanges && (
        <div className="rateCompactMutedNotice">
          ⚠️{" "}
          {t("rate_limit_unsaved_changes", {
            defaultValue: "You have unsaved changes.",
          })}
        </div>
      )}
    </div>
  );
}

export default RateLimitingSettings;